import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { ICastAPIIn } from 'skin/ts/Skin/interfaces/api/cast/ICastAPIIn';
import { ISkinAPIIn } from 'skin/ts/Skin/interfaces/api/skin/ISkinAPIIn';
import { APISkinMobileWeb } from 'tag/ts/tag/api/APISkinMobileWeb';
import { IAPI } from 'tag/ts/tag/interfaces/api/IAPI';
import { IDataApiIn } from 'tag/ts/tag/interfaces/api/IDataApiIn';
import { ISkinMobileWebAPIIn } from 'tag/ts/tag/interfaces/api/ISkinMobileWebAPIIn';
import { ITagAPIIn } from 'tag/ts/tag/interfaces/api/ITagAPIIn';
import { IVideoApiIn } from 'tag/ts/tag/interfaces/api/IVideoApiIn';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { TAPIArgument } from 'tag/ts/tag/types/TAPIArgument';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Tag API class
 */
export class API implements IAPI {
	/**
	 * @description Reference to current Tag instance
	 * @type {ITag}
	 */
	readonly #tag: ITag;

	/**
	 * @description Mobile Web Skin API. Used instead of desktop skin API instance
	 * @type {APISkinMobileWeb}
	 */
	readonly #mobileWebSkinAPI: APISkinMobileWeb;

	/**
	 * @description Stored logger instance
	 * @type {ILogger}
	 */
	#logger: ILogger;

	/**
	 * @description Registry for plugin API namespaces. Plugins register their APIs here via registerPluginAPI().
	 * @type {Map<string, unknown>}
	 */
	readonly #pluginAPIs: Map<string, unknown> = new Map();

	constructor( argument: TAPIArgument ) {
		this.#tag = argument?.tag;
		this.#logger = LogService.create( { prefixes: [ 'SmartTag:', 'Tag:', this.#tag?.uuid, this?.name ] } );

		// instantiate skin API for mobile web
		this.#mobileWebSkinAPI = (
			window.SmartTag.capabilities.isDesktop === false
				? new APISkinMobileWeb( { tag: this.#tag } )
				: undefined
		);
	}

	get name(): string {
		return 'API';
	}

	/**
	 * @description Returns reference to Utilities
	 */
	get utils() {
		return window.SmartTag.utilities;
	}

	/**
	 * @description TAG application API
	 * @return {ITagAPIIn}
	 */
	get TAG(): ITagAPIIn {
		return this.#tag?.controller?.tagAPI?.IN;
	}

	/**
	 * @description Video related API
	 */
	get VIDEO(): IVideoApiIn {
		return this.#tag?.controller?.videoAPI?.IN;
	}

	/**
	 * @description Chromecast API
	 */
	get CAST(): ICastAPIIn {
		return this.#tag?.view?.skin?.API?.CAST_IN;
	}

	/**
	 * @description Skin API. Replaced by Mobile Web Skin simulation API class on mobile devices
	 */
	get SKIN(): ISkinAPIIn | ISkinMobileWebAPIIn {
		// return different Skin API instances doe desktop and mobile web
		switch ( window.SmartTag.capabilities.isDesktop ) {
			case true: // desktop
				return this.#tag?.view?.skin?.API?.SKIN_IN;
			case false: // mobile web
				return this.#mobileWebSkinAPI?.IN;
		}
		// otherwise, return undefined
		return undefined;
	}

	/**
	 * @description Returns player adapter API
	 * @returns {IPlayerAdapterAPI | undefined}
	 */
	get PLAYER() {
		return this.#tag?.controller?.playerManager?.player;
	}

	/**
	 * @description Returns Data Service API namespace (tag.API.DATA). Undefined until the Data Service plugin registers.
	 */
	get DATA(): IDataApiIn | undefined {
		return this.getPluginAPI( 'DATA' ) as IDataApiIn | undefined;
	}

	/**
	 * @description Registers a plugin API namespace. Called by plugins during install() to expose APIs via tag.API.
	 * @param {string} name - Plugin API namespace name (e.g. 'DATA')
	 * @param {unknown} api - Plugin API object to expose
	 */
	registerPluginAPI( name: string, api: unknown ): void {
		this.#pluginAPIs.set( name, api );
	}

	/**
	 * @description Retrieves a registered plugin API namespace.
	 * @param {string} name - Plugin API namespace name
	 * @returns {unknown | undefined}
	 */
	getPluginAPI( name: string ): unknown | undefined {
		return this.#pluginAPIs.get( name );
	}
}